import { z } from 'zod';

export const MAX_AUDIO_BYTES = 1024 ** 3;
export const MULTIPART_PART_BYTES = 16 * 1024 ** 2;
export const MULTIPART_CONCURRENCY = 4;
export const UPLOAD_SESSION_HOURS = 24;
export const PART_URL_MINUTES = 60;
export const PLAYBACK_URL_HOURS = 3;

export const trackStatusSchema = z.enum([
  'uploading',
  'processing',
  'ready',
  'failed',
  'trashed',
]);

const acceptedMp3Types = new Set([
  'audio/mpeg',
  'audio/mp3',
  'audio/mpeg3',
  'audio/x-mpeg',
]);

export const createUploadSchema = z
  .object({
    filename: z.string().trim().min(1).max(255),
    contentType: z.string().trim().toLowerCase(),
    sizeBytes: z.number().int().positive().max(MAX_AUDIO_BYTES),
    lastModified: z.number().int().nonnegative(),
  })
  .superRefine((value, context) => {
    if (!value.filename.toLowerCase().endsWith('.mp3')) {
      context.addIssue({
        code: 'custom',
        path: ['filename'],
        message: 'Only .mp3 files are accepted',
      });
    }

    if (!acceptedMp3Types.has(value.contentType)) {
      context.addIssue({
        code: 'custom',
        path: ['contentType'],
        message: 'Only MPEG Layer III audio is accepted',
      });
    }
  });

const optionalNullableText = (maxLength: number) =>
  z
    .union([z.string().max(maxLength), z.null()])
    .optional()
    .transform((value) => {
      if (value === undefined || value === null) return value;
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    });

export const trackPatchSchema = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    artist: optionalNullableText(200),
    album: optionalNullableText(200),
    genre: optionalNullableText(100),
    description: optionalNullableText(2_000),
    recordedYear: z.number().int().min(1_000).max(9_999).nullable().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be supplied',
  });

export type TrackStatus = z.infer<typeof trackStatusSchema>;
export type CreateUploadInput = z.infer<typeof createUploadSchema>;
export type TrackPatchInput = z.input<typeof trackPatchSchema>;
export type TrackPatch = z.output<typeof trackPatchSchema>;
